import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useFetchTranslationLabels } from "../../hooks/translation/useFetchTranslationLabels";
import { FetchTranslationExportsParams, TranslationMode } from "../../api/fetchTranslationLabels";
import Spinner from "../../components/ui/Spinner";
import { useTitle } from "../../components/TitleContext";
import TableData from "../../components/ui/Table";
import { getTranslationLabelColumns, getTranslationLabelHiddenColumns } from "./CellRenderers/TranslationLabelColumns";
import { Label } from "../../model/Label";
import { useConfirmDialog } from "../../hooks/useConfirm";
import { useSendTranslationToReview } from "../../hooks/translation/useSendTranslationToReview";
import ErrorUtils from "../../utils/ApiUtils/ErrorUtils";
import TranslationStats, { LabelFilter } from "../../components/translation/statistics/TranslationStats";
import { useTranslateLabel } from "../../hooks/translation/useTranslateLabel";
import { Translation } from "../../model/Translation";
import { getsylobProduct, removeSnapshotIfExists } from "../../utils/ApiUtils/TranslationHelper";

interface TranslationLabelsProps {
  mode: TranslationMode;
}

const TranslationLabels: React.FC<TranslationLabelsProps> = ({ mode }) => {
  const { translationId } = useParams();
  const { setSubtitle } = useTitle();
  const idTranslation = parseInt(translationId!);
  const confirm = useConfirmDialog();
  const navigate = useNavigate();
  const location = useLocation();
  const translation = location.state?.translation as Translation;
  const [activeStatsFilter, setActiveStatsFilter] = useState<LabelFilter>("pending");
  const [statsRefetchFn, setStatsRefetchFn] = useState<(() => void) | null>(null);
  const sendTranslationToReview = useSendTranslationToReview(() => navigate("/translation"));
  const translateMutation = useTranslateLabel({
    onSuccess: (updatedLabel: Label) => {
      refetch();
      statsRefetchFn?.();
    },
  });
  const tableKey = useMemo(() => `translationTable-${activeStatsFilter}`, [activeStatsFilter]);
  const params: FetchTranslationExportsParams = {
    translationId: idTranslation,
    filter: activeStatsFilter,
  };
  const { data: labels, isLoading, isError, error, refetch } = useFetchTranslationLabels(params, mode);
  const handleStatsFilterChange = useCallback((filter: LabelFilter) => {
    setActiveStatsFilter(filter);
  }, []);
  const handleLabelSave = useCallback(async (label: Label, forceMode = false) => {
    translateMutation.mutate(forceMode ? { ...label, pending: true } : label);
  }, [translateMutation]);
  const handleTranslationReview = async () => {
    const isConfirmed = await confirm({
      title: "Send to review",
      message: "Are you sure you want to send this translation for review? ",
    });
    if (isConfirmed) {
      sendTranslationToReview.mutate(idTranslation);
    }
  };

  // Callback to receive the stats refetch function
  const handleStatsRefetchReady = useCallback((refetchFn: () => void) => {
    setStatsRefetchFn(() => refetchFn);
  }, []);

  useEffect(() => {
    let productName = getsylobProduct(translation?.product);
    let productTechnicalVersion = removeSnapshotIfExists(translation?.mavenVersion);
    if (productName && productTechnicalVersion) {
      setSubtitle(productName.toUpperCase() + " " + productTechnicalVersion);
    }
  }, [setSubtitle]);

  const pageLoadStartTime =performance.now();

  useEffect(() => {
    if (labels && !isLoading) {
      const totalTime = performance.now() - pageLoadStartTime;
      console.log(`PAGE DONE: ${labels.length} rows in ${totalTime.toFixed(0)}ms`);
    }
  }, [labels, isLoading]);


  if (isLoading) {
    return <Spinner />;
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-red-500"> {ErrorUtils.getErrorMessage(error)} </p>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col relative">
      <div className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <TranslationStats
          translationId={idTranslation}
          activeFilter={activeStatsFilter}
          onFilterChange={handleStatsFilterChange}
          onClose={() => navigate("/translation")}
          onSendForReview={handleTranslationReview}
          onStatsRefetchReady={handleStatsRefetchReady}
        />
      </div>
      <div className="flex-1 overflow-hidden">
        <TableData
          key={tableKey}
          tableName="translationTable"
          rows={labels || []}
          columns={getTranslationLabelColumns(handleLabelSave)}
          hiddenColumns={getTranslationLabelHiddenColumns()}
          pageSize={[25, 50, 75, 100]}
          dynamicRowHeight
          enhancedStyling={true}
          refreshFunction={refetch}
        />
      </div>
    </div>
  );
};

export default TranslationLabels;
